<script>
    import { onMount } from "svelte";
    import { get } from "svelte/store";
    import { push } from "svelte-spa-router";
    import { Preferences } from "@capacitor/preferences";
    import NavBar from "../../components/NavBar.svelte";
    import SupplyPlanSearch from "./SupplyPlanSearch.svelte";
    import SupplyPlanList from "./SupplyPlanList.svelte";
    import SupplyPlanTreeModal from "./SupplyPlanTreeModal.svelte";
    import ColumnSettingModal from "./ColumnSettingModal.svelte";
    import {
        supplyPlanSearch,
        rawProductsStore,
        initSupplyPlanMeta,
        fetchSupplyPlanFromDB,
        loadSupplyPlanSettings,
        planWeeks,
        defaultWeekRange,
    } from "./store.js";
    import toast from "svelte-french-toast";

    let isTreeModalOpen = false;
    let isColumnModalOpen = false;

    async function handleReset() {
        const defaultBase = $planWeeks && $planWeeks.length > 0 ? $planWeeks[0].code : "";
        const defaultCompare = $planWeeks && $planWeeks.length > 1 ? $planWeeks[1].code : defaultBase;
        // 컬럼설정에서 지정한 기본 조회구간(기본: 현재주차 기준 과거1주~미래19주) 적용
        const weekRange = get(defaultWeekRange);
        supplyPlanSearch.set({
            excludeZero: true,
            isMultiWeek: false,
            monthSum: false,
            startDate: weekRange.startDate,
            endDate: weekRange.endDate,
            startYear: weekRange.startYear,
            endYear: weekRange.endYear,
            startWeek: weekRange.startWeek,
            endWeek: weekRange.endWeek,
            baseWeek: defaultBase,
            compareWeek: defaultCompare,
            itemKeyword: "",
            productType: "",
            itemGroup: "",
            modelIntegration: "",
            model: "",
            itemCode: "",
            category: "ALL",
            region: "ALL",
            team: "ALL",
            customer: "",
            isExpanded: true,
            selectedItemGroups: [],
            selectedProductTypes: [],
            selectedModels: [],
            selectedGroupModels: [],
            selectedTreeIds: [],
        });
        // 표시 컬럼/항목 설정은 컬럼설정에 저장된 값을 그대로 유지
        await fetchSupplyPlanFromDB({
            startDate: weekRange.startDate,
            endDate: weekRange.endDate,
            startYear: weekRange.startYear,
            endYear: weekRange.endYear,
            startWeek: weekRange.startWeek,
            endWeek: weekRange.endWeek,
            baseWeek: defaultBase,
            compareWeek: defaultCompare,
            isMultiWeek: false,
            monthSum: false,
            itemKeyword: "",
            productType: "",
            itemGroup: "",
            modelIntegration: "",
            model: "",
            selectedItemGroups: [],
            selectedProductTypes: [],
            selectedModels: [],
            selectedGroupModels: [],
            selectedTreeIds: [],
        });
        toast.success("조회 조건 및 공급계획이 초기화되었습니다.", { duration: 1500 });
    }

    function openTreeModal() {
        isTreeModalOpen = true;
    }

    function openColumnModal() {
        isColumnModalOpen = true;
    }

    onMount(async () => {
        // 권한(tbt_menu_at) 확인: 'Y'(경영정보) 또는 'A'(전체) 접근 허용
        try {
            const { value } = await Preferences.get({ key: "retechInfo" });
            if (!value) {
                push("/Login");
                return;
            }
            const userData = JSON.parse(value);
            const rawTbt = userData.tbt_menu_at;
            let tbtMenuAt = "N";
            if (rawTbt) {
                tbtMenuAt = String(rawTbt).trim().toUpperCase();
            } else if (userData.login_id === "admin") {
                tbtMenuAt = "A";
            }

            if (tbtMenuAt !== "Y" && tbtMenuAt !== "A" && userData.login_id !== "admin") {
                toast.error("경영정보 메뉴 접근 권한이 없습니다.", { duration: 2500 });
                push("/Menu");
                return;
            }
        } catch (e) {
            push("/Menu");
            return;
        }

        // 저장된 컬럼설정(표시 컬럼/항목/기본 조회구간) 불러오기 → DB 기준주차/제품분류/기종통합 메타데이터 및 초기 공급계획 조회
        try {
            await loadSupplyPlanSettings();
            await initSupplyPlanMeta();
            await fetchSupplyPlanFromDB(get(supplyPlanSearch));
        } catch (e) {
            console.error("초기 데이터 로딩 오류:", e);
        }

        // ESC 키로 모달 닫기
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                isTreeModalOpen = false;
                isColumnModalOpen = false;
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    });
</script>

<div class="page-container">
    <NavBar activeMenu="공급계획(PSI)" on:reset={handleReset} on:menuReset={handleReset} />

    <div class="content-wrapper">
        <!-- 상단 조회 조건 영역 (화면 상단 풀 너비 고정) -->
        <SupplyPlanSearch
            {openTreeModal}
            {openColumnModal}
            on:search={() => {}}
        />

        <main class="main-layout">
            <!-- 하단 공급계획 리스트 (PSI 그리드) -->
            <SupplyPlanList />
        </main>
    </div>

    <!-- 모달 컴포넌트 -->
    <SupplyPlanTreeModal
        isOpen={isTreeModalOpen}
        on:close={() => isTreeModalOpen = false}
    />

    <ColumnSettingModal
        isOpen={isColumnModalOpen}
        on:close={() => isColumnModalOpen = false}
    />
</div>

<style>
    .page-container {
        display: flex;
        flex-direction: column;
        width: 100vw;
        height: 100vh;
        background: #f1f5f9;
        overflow: hidden;
    }
    .content-wrapper {
        flex: 1;
        display: flex;
        flex-direction: column;
        min-height: 0;
        overflow: hidden;
    }
    .main-layout {
        flex: 1;
        display: flex;
        flex-direction: column;
        padding: 10px;
        gap: 0;
        min-height: 0;
        overflow: hidden;
    }
</style>
